// Manual Booking admin service (SMB-3541).
//
// Records a flight/hotel reservation already booked + paid outside Zenvoya. Runs
// on the API gateway like every other beadmin domain: encrypted request +
// decrypted response, beadmin auth headers, on /app/v1/beadmin paths (via
// apiClient). Gateway routes forward to the booking engine's manual-booking API:
//   createManualFlightBooking -> POST /app/v1/beadmin/manual-booking/flights
//                                (engine POST /be/v1/manual-booking/flights)
//   createManualHotelBooking  -> POST /app/v1/beadmin/manual-booking/hotels
//                                (engine POST /be/v1/manual-booking/hotels)
//
// Envelope: the engine returns HTTP 201 with { status, message, data:{ itineraryID,
// transactionId, tid, ... } }. Through the gateway the body may be wrapped as
// { statusCode, message, data: { status, message, data } } (as the flight-booking
// route does). `unwrap` peels that off so callers read the engine envelope directly.
// Branch on the returned `.status` (201 ok | 400 validation | 409 duplicate | 500).
// See the SMB-3541 hand-off doc.
//
// NOTE: like the promo routes, the gateway mapping for /manual-booking/* may still
// be pending. This file is the single place to switch to a direct engine path if
// the gateway is not wired.
import { BE_ADMIN_BASE } from "./environment";
import { apiPost } from "./apiClient";
import { getUserDetails } from "./authSession";

const MANUAL_BOOKING_BASE = `${BE_ADMIN_BASE}/manual-booking`;

// Who is recording the booking — mirrors getAdminId() in promoService.js.
// Used as the createdBy / ops-agent id.
export const getAdminId = () => {
  const u = getUserDetails();
  return u?.email || u?.userId || u?.id || "admin";
};

// Normalize to the engine envelope { status, message, data }. The gateway may
// preserve that shape as-is, or wrap it as { statusCode, message, data:{...} }.
// Detect the wrapper by the inner `.data.status` and peel it off.
const unwrap = (res) =>
  res && res.data && res.data.status !== undefined ? res.data : res;

/**
 * Save a manual flight booking.
 * @param {object} payload built by buildFlightPayload() (userId, amounts, sectors, passengers)
 * @returns {Promise<{status:number,message:string,data:object}>}
 */
export const createManualFlightBooking = (payload, opts = {}) =>
  apiPost(`${MANUAL_BOOKING_BASE}/flights`, payload, opts).then(unwrap);

/**
 * Save a manual hotel booking.
 * @param {object} payload built by buildHotelPayload() (userId, amounts, hotels, guests)
 * @returns {Promise<{status:number,message:string,data:object}>}
 */
export const createManualHotelBooking = (payload, opts = {}) =>
  apiPost(`${MANUAL_BOOKING_BASE}/hotels`, payload, opts).then(unwrap);

export default {
  createManualFlightBooking,
  createManualHotelBooking,
  getAdminId,
};
